import { useSession } from 'next-auth/react';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  QueryId,
  TOTAL_PRICE_DEPENDENT_QUERIES,
} from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationAddPreBookingDiscountCodeData =
  Booking.Components['PutDiscountCodeResponse'];

interface UseMutationAddPreBookingDiscountCodeError {
  errors: any;
  message?: string;
}

interface UseMutationAddPreBookingDiscountCodeParameters {
  brand?: Content.Properties.Brand;
  discountCode: string;
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const parseResponseBody = async (response: Response) => {
  const responseText = await response.text();

  if (!responseText) return null;

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText, result: false };
  }
};

const useMutationAddPreBookingDiscountCode = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const mutation: UseMutationResult<
    UseMutationAddPreBookingDiscountCodeData,
    UseMutationAddPreBookingDiscountCodeError,
    UseMutationAddPreBookingDiscountCodeParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/discount-codes/add-discount-code',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            discountCode: parameters.discountCode,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
            ...(session?.user.accessToken
              ? { ['Authorization']: `Bearer ${session?.user.accessToken}` }
              : {}),
          },
          method: 'PUT',
        },
      )
        .then(async response => {
          const responseBody = await parseResponseBody(response);

          if (response.ok) return responseBody;

          if (response.status === 400 && responseBody) return responseBody;

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'bookingFlow.useMutationAddPreBookingDiscountCode',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (data, parameters) => {
        if (!data.result) return;

        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        TOTAL_PRICE_DEPENDENT_QUERIES.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationAddPreBookingDiscountCode;
